'use client';

import { useQuery } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

import { AuthApi } from '@/api';
import { useAccessToken } from '@/hooks/useAccessToken';
import { useLoginUrlData } from '@/hooks/useLoginUrlData';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';

import {
  KB_PAY_CUSTOM_SCHEME,
  KB_PAY_CUSTOM_SEARCH_PARAMS,
} from '../_utils/reserved-keywords';

export const KbAppController = () => {
  const pathname = usePathname();
  const isKbWebview = useDeviceInfoStore(
    (state) => state.webviewType === WEBVIEW_TYPE.KB_PAY,
  );

  const { loginParams } = useLoginUrlData();

  const kbId = new URLSearchParams(loginParams).get(
    KB_PAY_CUSTOM_SEARCH_PARAMS.KB_ID,
  );

  useEffect(() => {
    if (!isKbWebview) return;

    const listeners: Array<[HTMLAnchorElement, (e: Event) => void]> = [];

    document.querySelectorAll('a').forEach((el) => {
      const href = el.getAttribute('href');

      if (!href) return;

      if (!href.startsWith('https')) return;

      if (href.startsWith(location.origin)) return;

      const handler = (e: Event) => {
        e.preventDefault();

        window.location.href = `${KB_PAY_CUSTOM_SCHEME.EXTERNAL_PREFIX}${href}`;
      };

      el.addEventListener('click', handler);
      listeners.push([el, handler]);
    });

    return () => {
      listeners.forEach(([el, handler]) =>
        el.removeEventListener('click', handler),
      );
    };
  }, [isKbWebview, pathname]);

  const isLogin = useAuthStore((state) => state.isLogin);

  const { accessToken, refreshToken } = useAccessToken();

  const isEnabledKbPayMemberCheck = !!(
    isLogin &&
    accessToken &&
    refreshToken &&
    kbId
  );

  useQuery({
    enabled: isEnabledKbPayMemberCheck,
    queryKey: [
      WEBVIEW_TYPE.KB_PAY,
      kbId,
      accessToken,
      refreshToken,
      isEnabledKbPayMemberCheck,
    ],
    queryFn: () => {
      if (!isEnabledKbPayMemberCheck) return;

      AuthApi['post:/wello/v2/auth/member/kb-member-check']({
        body: {
          accessToken: accessToken,
          refreshToken: refreshToken,
          externalMemberId: kbId,
        },
      });
    },
  });

  return <></>;
};
